import React, { useContext } from 'react'
import { StyleSheet, Text, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native'
import Loader from '../../loader'
import { ThemeContext } from '../../context/ThemeContext'
import { textStyles } from '../../../style'
import Haptics from '../../haptics'

export type ThemedButtonVariant = 'primary' | 'secondary' | 'icon' | 'cancel' | 'link'
type HapticLevel = 'none' | 'Light' | 'Medium' | 'Heavy' | 'Soft' | 'Rigid'

type ThemedButtonProps = {
  text: string
  onPress: () => void
  disabled?: boolean
  loading?: boolean
  containerStyle?: ViewStyle
  style?: ViewStyle
  textStyle?: TextStyle
  hapticLevel?: HapticLevel
  Icon?: React.FC
} & (
  {
    variant: 'primary' | 'secondary' | 'cancel' | 'link'
    Icon?: never
  } | {
    variant: 'icon'
    Icon: React.FC
  }
)

export const ThemedButton: React.FC<ThemedButtonProps> = ({
  text,
  onPress,
  disabled,
  loading,
  variant = 'primary',
  containerStyle,
  style,
  textStyle,
  hapticLevel,
  Icon
}: ThemedButtonProps) => {
  const theme = useContext(ThemeContext)
  const {
    primary,
    white,
    disabled: disabledColor,
    bgMain,
    borderColor,
    textAlt,
    textMain,
    buttonAlt,
    lightAccent,
    darkAccent
  } = theme.theme.colors

  const getButtonStyle = (): ViewStyle => {
    switch (variant) {
      case 'primary':
        return { backgroundColor: disabled ? disabledColor : primary, borderWidth: 0 }
      case 'secondary':
        return { backgroundColor: bgMain, borderColor, borderWidth: 1 }
      case 'icon':
        return { backgroundColor: buttonAlt, borderWidth: 0 }
      case 'cancel':
        return { backgroundColor: 'transparent' }
      case 'link':
        return { backgroundColor: 'transparent' }
      default:
        return { backgroundColor: primary }
    }
  }

  const getTextColor = (): string => {
    switch (variant) {
      case 'primary':
        return white
      case 'secondary':
        return textMain
      case 'cancel':
        return textAlt
      case 'link':
        return primary
      default:
        return white
    }
  }

  const triggerImpact = (level?: HapticLevel) => {
    const chosenLevel = level || variant
    switch (chosenLevel) {
      case 'Heavy':
      case 'primary':
        Haptics.impactHeavy()
        break
      case 'Medium':
      case 'secondary':
      case 'icon':
        Haptics.impactMedium()
        break
      case 'Light':
      case 'link':
        Haptics.impactLight()
        break
      default:
        break
    }
  }

  const handlePress = () => {
    if (hapticLevel !== 'none') {
      triggerImpact(hapticLevel)
    }
    onPress()
  }

  const getLoaderColors = () => {
    switch (variant) {
      case 'primary':
        return { color1: white, color2: white, color3: white }
      default:
        return { color1: primary, color2: lightAccent, color3: darkAccent }
    }
  }

  return (
    <>
      {variant === 'link' ? (
        <TouchableOpacity onPress={handlePress} disabled={disabled || loading} hitSlop={10}>
          <Text style={[textStyles.body, { color: getTextColor() }]}>{text}</Text>
        </TouchableOpacity>
      ) : (
        <View style={[styles.buttonWrapper, containerStyle]}>
          <TouchableOpacity
            style={[styles.button, {height: variant !== 'cancel' ? 42 : undefined}, getButtonStyle(), style]}
            onPress={handlePress}
            disabled={disabled || loading}
          >
            {loading ? (
              <Loader size={'small'} colors={getLoaderColors()} />
            ) :(
              <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4}}>
                {Icon ? <Icon /> : null}
                <Text style={[textStyles.body, { color: getTextColor() }, textStyle]}>{text}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      )}
    </>
  )
}

const styles = StyleSheet.create({
  buttonWrapper: {
    width: '100%',
  },
  button: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
})


